/**
 * cart-upsell.js — Marmoset Found
 * Fixes applied:
 * 1. Shipping bar threshold corrected: 2000 → 20000 (cents)
 * 2. Double-init race condition fixed with _isAdding flag
 * 3. cart:update loop broken — skips re-init during active add
 * 4. Card removal guarded against detached DOM elements
 * 5. fetchCollectionFallback now receives params from init() safely
 */

class CartUpsell {
  constructor() {
    this.upsellSection = document.getElementById('mf-upsell-section');
    this.upsellContainer = document.getElementById('mf-upsell-products');
    this.giftWrapBtn = document.getElementById('mf-gift-wrap-btn');
    this.offerBanner = document.getElementById('mf-offer-banner');
    this._isAdding = false; // FIX 2: guard flag for active add operations

    if (this.upsellSection && this.upsellContainer) this.init();
    if (this.giftWrapBtn) this.initGiftWrap();
    this.initCloseBtn();
  }

  // ─── Close button ──────────────────────────────────────
  initCloseBtn() {
    const closeBtn = document.getElementById('mf-upsell-close');
    if (!closeBtn) return;
    closeBtn.addEventListener('click', () => {
      const section = document.getElementById('mf-upsell-section');
      if (!section) return;
      section.style.transition = 'opacity 0.3s';
      section.style.opacity = '0';
      setTimeout(() => section.remove(), 300);
    });
  }

  // ─── Main init ─────────────────────────────────────────
  async init() {
    const productId = this.upsellSection.dataset.productId;

    // FIX 5: capture dataset values now, while element is in DOM
    const collectionHandle = this.upsellSection.dataset.collectionHandle;
    const productType = this.upsellSection.dataset.productType;

    if (!productId) return;

    try {
      const [recoRes, cartRes] = await Promise.all([
        fetch(`/recommendations/products.json?product_id=${productId}&limit=8&intent=related`),
        fetch('/cart.js'),
      ]);

      const [recoData, cart] = await Promise.all([
        recoRes.json(),
        cartRes.json(),
      ]);

      const cartProductIds = cart.items.map(i => i.product_id);

      let products = (recoData.products || []).filter(
        p => !cartProductIds.includes(p.id) && p.available
      );

      // ── Fallback: pull from collection if API returned nothing ──
      if (products.length === 0) {
        // FIX 5: pass pre-captured values instead of re-querying DOM
        products = await this.fetchCollectionFallback(productId, cartProductIds, collectionHandle, productType);
      }

      if (products.length === 0) {
        this.upsellSection.style.display = 'none';
        return;
      }

      this.renderCards(products.slice(0, 4));
      this.initArrows();

    } catch (e) {
      console.warn('[MF] Upsell init failed:', e);
      this.upsellSection.style.display = 'none';
    }
  }

  // ─── Collection fallback ───────────────────────────────
  // FIX 5: accepts collectionHandle and productType as params
  async fetchCollectionFallback(productId, cartProductIds, collectionHandle, productType) {
    const urls = [
      collectionHandle && `/collections/${collectionHandle}/products.json?limit=10`,
      productType && `/collections/${productType}/products.json?limit=10`,
    ].filter(Boolean);

    for (const url of urls) {
      try {
        const res = await fetch(url);
        if (!res.ok) continue;
        const data = await res.json();
        const filtered = (data.products || []).filter(
          p => p.id !== parseInt(productId) && !cartProductIds.includes(p.id) && p.available
        );
        if (filtered.length > 0) return filtered;
      } catch (e) {
        console.warn('[MF] Collection fallback failed for:', url, e);
      }
    }

    return [];
  }

  // ─── Render cards ──────────────────────────────────────
  renderCards(products) {
    this.upsellContainer.innerHTML = products.map(p => {
      const variant = p.variants.find(v => v.available) || p.variants[0];
      const isMultiVariant = p.variants.length > 1;
      const price = this.formatMoney(variant.price);
      const img = p.featured_image
        ? p.featured_image.replace(/\.(\w+)$/, '_280x280.$1')
        : '';

      return `
        <div class="mf-upsell-card">
          <a href="${p.url}" class="mf-upsell-card__img-wrap" tabindex="-1" aria-hidden="true">
            ${img ? `<img src="${img}" alt="${p.title}" loading="lazy" width="130" height="130">` : ''}
          </a>
          <p class="mf-upsell-card__title">${p.title}</p>
          <p class="mf-upsell-card__price">${price}</p>
          <button
            class="mf-upsell-card__btn"
            data-variant-id="${variant.id}"
            data-product-url="${p.url}"
            data-multi-variant="${isMultiVariant}"
            aria-label="${isMultiVariant ? 'View' : 'Add'} ${p.title}"
          >
            ${isMultiVariant ? 'Choose options' : 'Add to cart'}
          </button>
        </div>
      `;
    }).join('');

    this.upsellContainer.querySelectorAll('.mf-upsell-card__btn').forEach(btn => {
      btn.addEventListener('click', e => {
        if (btn.dataset.multiVariant === 'true') {
          window.location.href = btn.dataset.productUrl;
        } else {
          this.handleQuickAdd(e);
        }
      });
    });
  }

  // ─── Arrows ────────────────────────────────────────────
  initArrows() {
    const track = document.getElementById('mf-upsell-products');
    const left = document.getElementById('mf-arrow-left');
    const right = document.getElementById('mf-arrow-right');
    if (!track || !left || !right) return;
    left.addEventListener('click', () => track.scrollBy({ left: -145, behavior: 'smooth' }));
    right.addEventListener('click', () => track.scrollBy({ left: 145, behavior: 'smooth' }));
  }

  // ─── Quick add ─────────────────────────────────────────
  async handleQuickAdd(e) {
    const btn = e.currentTarget;
    const variantId = btn.dataset.variantId;
    if (!variantId || btn.classList.contains('mf-upsell-card__btn--loading')) return;

    // FIX 2 & 3: signal that an add is in progress before any async work
    this._isAdding = true;

    btn.classList.add('mf-upsell-card__btn--loading');
    btn.textContent = '...';

    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: variantId, quantity: 1 }),
      });

      if (!res.ok) throw new Error('Add to cart failed');
      const addedItem = await res.json();

      btn.classList.remove('mf-upsell-card__btn--loading');
      btn.classList.add('mf-upsell-card__btn--added');
      btn.textContent = 'Added ✓';

      await this.refreshCart(addedItem, variantId);

      // FIX 4: guard card removal against detached DOM elements
      setTimeout(() => {
        const card = btn.closest('.mf-upsell-card');
        if (card && card.isConnected) {
          card.style.transition = 'opacity 0.3s';
          card.style.opacity = '0';
          setTimeout(() => {
            if (card.isConnected) card.remove();
          }, 300);
        }
        // FIX 2: clear the adding flag after card is gone
        this._isAdding = false;
      }, 1200);

    } catch (e) {
      console.warn('[MF] Quick add failed:', e);
      btn.classList.remove('mf-upsell-card__btn--loading');
      btn.textContent = 'Add to cart';
      // FIX 2: always clear flag on failure too
      this._isAdding = false;
    }
  }

  // ─── Cart refresh ──────────────────────────────────────
  async refreshCart(addedItem, variantId) {
    try {
      const sectionId = document.querySelector('cart-items-component[data-section-id]')?.dataset?.sectionId;
      if (!sectionId) return;

      const sectRes = await fetch(`/?sections=${sectionId}`);
      const sections = await sectRes.json();

      const cartEvent = new CustomEvent('cart:update', {
        bubbles: true,
        detail: {
          resource: addedItem,
          sourceId: 'mf-upsell',
          data: {
            itemCount: addedItem.quantity,
            variantId: String(variantId),
            sections,
          },
        },
      });
      document.dispatchEvent(cartEvent);

      this.refreshShippingBar();
    } catch (e) {
      console.warn('[MF] Cart refresh failed:', e);
    }
  }

  // ─── Combined bar refresh ──────────────────────────────
  async refreshShippingBar() {
    try {
      const cartRes = await fetch('/cart.js');
      const cart = await cartRes.json();
      this.refreshCombinedBar(cart);
    } catch (e) {
      console.warn('[MF] Combined bar refresh failed:', e);
    }
  }

  // ─── Combined bar refresh implementation ───────────────
  refreshCombinedBar(cart) {
    try {
      const gwpThreshold = 12000;
      const shippingThreshold = 22000;
      let qualifyingGwpTotal = 0;

      if (cart && cart.items) {
        cart.items.forEach(item => {
          const title = (item.title || item.product_title || '').toLowerCase();
          const handle = (item.handle || '').toLowerCase();
          const type = (item.product_type || '').toLowerCase();

          let isCloud = handle.includes('cloud') || title.includes('cloud') || type.includes('cloud');
          let isSharing = handle.includes('sharing') || title.includes('sharing') || type.includes('sharing');
          let isGift = title.includes('hand cream') || title.includes('vetiver') || item.price === 0;

          if (isCloud && !isSharing && !isGift) {
            qualifyingGwpTotal += (item.final_line_price !== undefined ? item.final_line_price : (item.price * item.quantity));
          }
        });
      }

      const gwpUnlocked = qualifyingGwpTotal >= gwpThreshold;
      const shippingUnlocked = cart.total_price >= shippingThreshold;

      const gwpRemaining = Math.max(0, gwpThreshold - qualifyingGwpTotal);
      const shippingRemaining = Math.max(0, shippingThreshold - cart.total_price);

      const pct = Math.min(100, Math.round((cart.total_price / shippingThreshold) * 100));

      const fill = document.querySelector('.mf-combined-bar__fill');
      const msg = document.querySelector('.mf-combined-bar__message');
      const gwpNode = document.querySelector('.mf-combined-bar__node--gwp');
      const shippingNode = document.querySelector('.mf-combined-bar__node--shipping');

      if (fill) {
        fill.style.width = pct + '%';
        if (shippingUnlocked) fill.classList.add('mf-combined-bar__fill--complete');
        else fill.classList.remove('mf-combined-bar__fill--complete');
      }

      if (gwpNode) {
        if (gwpUnlocked) gwpNode.classList.add('mf-combined-bar__node--unlocked');
        else gwpNode.classList.remove('mf-combined-bar__node--unlocked');
      }

      if (shippingNode) {
        if (shippingUnlocked) shippingNode.classList.add('mf-combined-bar__node--unlocked');
        else shippingNode.classList.remove('mf-combined-bar__node--unlocked');
      }

      if (msg) {
        if (gwpUnlocked && shippingUnlocked) {
          msg.innerHTML = '<span class="mf-combined-bar__icon">✦</span> You’ve unlocked <strong>Free Hand Cream</strong> & <strong>Free Shipping</strong> — enjoy!';
        } else if (gwpUnlocked) {
          msg.innerHTML = `<span class="mf-combined-bar__icon">✦</span> Hand Cream unlocked! Add <strong>${this.formatMoney(shippingRemaining)}</strong> more for <strong>Free Shipping</strong>`;
        } else if (shippingUnlocked) {
          msg.innerHTML = `<span class="mf-combined-bar__icon">✦</span> Free Shipping unlocked! Spend <strong>${this.formatMoney(gwpRemaining)}</strong> more on Cloud Collection for <strong>Free Hand Cream</strong>`;
        } else {
          msg.innerHTML = `Spend <strong>${this.formatMoney(gwpRemaining)}</strong> more on Cloud Collection for <strong>Free Hand Cream</strong> & <strong>${this.formatMoney(shippingRemaining)}</strong> for <strong>Free Shipping</strong>`;
        }
      }
    } catch (e) {
      console.warn('[MF] Combined bar refresh failed:', e);
    }
  }

  // ─── Gift wrap ─────────────────────────────────────────
  initGiftWrap() {
    this.giftWrapBtn.addEventListener('click', async () => {
      const variantId = this.giftWrapBtn.dataset.variantId;
      if (!variantId) return;

      this.giftWrapBtn.textContent = '...';
      this.giftWrapBtn.disabled = true;

      try {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: variantId, quantity: 1 }),
        });
        const addedItem = await res.json();

        this.giftWrapBtn.textContent = 'Added ✓';
        this.giftWrapBtn.classList.add('mf-offer-banner__btn--added');
        await this.refreshCart(addedItem, variantId);

        setTimeout(() => {
          if (this.offerBanner && this.offerBanner.isConnected) {
            this.offerBanner.style.transition = 'opacity 0.4s';
            this.offerBanner.style.opacity = '0';
            setTimeout(() => {
              if (this.offerBanner.isConnected) this.offerBanner.remove();
            }, 400);
          }
        }, 1500);

      } catch (e) {
        console.warn('[MF] Gift wrap add failed:', e);
        this.giftWrapBtn.textContent = 'Add $5';
        this.giftWrapBtn.disabled = false;
      }
    });
  }

  // ─── Format money ──────────────────────────────────────
  formatMoney(cents) {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD',
      minimumFractionDigits: 0,
    }).format(cents / 100);
  }
}

// ─── Payment button styling ────────────────────────────
function stylePaymentButtons() {
  const ctas = document.querySelector('.cart__ctas');
  if (!ctas) return;
  ctas.style.cssText = 'display:grid;grid-template-columns:1fr 1fr;gap:6px;';
  const checkout = ctas.querySelector('.cart__checkout-button');
  if (checkout) checkout.style.cssText = 'grid-column:1/-1;height:40px;font-size:12px;';
  const additional = ctas.querySelector('.cart__additional-checkout-buttons, .additional-checkout-buttons');
  if (additional) {
    additional.style.cssText = 'display:contents;';
    additional.querySelectorAll(':scope > *').forEach(btn => {
      btn.style.cssText = 'width:100%;min-width:0;height:36px;';
    });
  }
}

// ─── Reliable init via MutationObserver ───────────────
let upsellInitialised = false;
let activeInstance = null; // FIX 3: track the active instance

function tryInitUpsell() {
  const section = document.getElementById('mf-upsell-section');
  const container = document.getElementById('mf-upsell-products');
  if (!section || !container) return;

  // Only run if skeletons are still present (not yet loaded)
  if (!container.querySelector('.mf-upsell__skeleton')) return;

  // Guard against double-init within the same render cycle
  if (upsellInitialised) return;
  upsellInitialised = true;

  activeInstance = new CartUpsell();

  // Reset flag after a tick so future re-renders can re-init
  setTimeout(() => { upsellInitialised = false; }, 1000);
}

// Watch for the upsell section being injected or re-injected
const upsellObserver = new MutationObserver(() => {
  tryInitUpsell();
});

upsellObserver.observe(document.body, {
  childList: true,
  subtree: true,
});

// Fallback listeners
document.addEventListener('DOMContentLoaded', () => {
  tryInitUpsell();
  setTimeout(stylePaymentButtons, 800);
});

document.addEventListener('cart:update', () => {
  // FIX 3: skip re-init entirely if an add is currently in progress
  if (activeInstance?._isAdding) return;

  // Reset so next render triggers a fresh init
  upsellInitialised = false;
  setTimeout(() => {
    tryInitUpsell();
    stylePaymentButtons();
  }, 800);
});